import { useCallback, useEffect, useState } from 'react';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../config/api';
import type { FeedHealthEntry } from '../utils/dataFreshness';

/** Re-read the feed records while the page is open and visible. */
const FRESHNESS_POLL_MS = 60_000;

export interface DayActivity {
  date?: string;
  positions?: number;
  orders?: number;
}

export interface DataFreshnessInputs {
  feeds: FeedHealthEntry[] | null;
  enabledSources: string[];
  /** When the server read the feed records (not the browser clock). */
  checkedAt: string | null;
  telematicsActivity: DayActivity[];
  ordersActivity: DayActivity[];
  loading: boolean;
  error: string | null;
}

/**
 * Feed-health read model for the freshness panel:
 *   GET /ingestion/manage/feeds   — per-feed last success + learned usual gap
 *   GET /ingestion/manage/status  — org-enabled sources
 *   GET /fleet-intelligence/{vehicles,orders}/activity-by-day — newest day with data
 * Failures leave the affected input empty so the panel shows Unknown, never a guess.
 */
export function useDataFreshness(
  enabled = true,
  opts: { activity?: boolean } = {},
): DataFreshnessInputs & { reload: () => void } {
  // The two activity-by-day series are Athena queries. Callers that only need
  // the feed records (the Control Centre stamps) turn them off.
  const withActivity = opts.activity !== false;
  const [state, setState] = useState<DataFreshnessInputs>({
    feeds: null,
    enabledSources: [],
    checkedAt: null,
    telematicsActivity: [],
    ordersActivity: [],
    loading: enabled,
    error: null,
  });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const getJson = async (url: string) => {
      const res = await authenticatedFetch(url);
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    };
    void (async () => {
      const [feeds, status, tele, orders] = await Promise.allSettled([
        getJson(API_ENDPOINTS.ingestionManage.feeds()),
        getJson(API_ENDPOINTS.ingestionManage.status()),
        withActivity ? getJson(API_ENDPOINTS.fleetIntelligence.vehicles.activityByDay(7)) : Promise.reject(new Error('skipped')),
        withActivity ? getJson(API_ENDPOINTS.fleetIntelligence.orders.activityByDay(7)) : Promise.reject(new Error('skipped')),
      ]);
      if (cancelled) return;
      setState({
        feeds: feeds.status === 'fulfilled' && Array.isArray(feeds.value?.feeds) ? feeds.value.feeds : null,
        checkedAt: feeds.status === 'fulfilled' ? (feeds.value?.checked_at ?? null) : null,
        enabledSources: status.status === 'fulfilled' && Array.isArray(status.value?.enabledSources)
          ? status.value.enabledSources : [],
        telematicsActivity: tele.status === 'fulfilled' && Array.isArray(tele.value?.series) ? tele.value.series : [],
        ordersActivity: orders.status === 'fulfilled' && Array.isArray(orders.value?.series) ? orders.value.series : [],
        loading: false,
        error: feeds.status === 'rejected' ? 'Could not check how up to date the data is' : null,
      });
    })();
    return () => { cancelled = true; };
  }, [enabled, tick, withActivity]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') reload();
    }, FRESHNESS_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, reload]);

  return { ...state, reload };
}
